import React from 'react';

import PropTypes from 'prop-types';
import styled from 'styled-components';

import PlusIcon from '~/icons/components/PlusIcon';
import { Link } from '~/techstyle-shared/next-routes';
import {
  SkeletonLine,
  useBreakpoint,
} from '~/techstyle-shared/react-components';
import useId from '~/techstyle-shared/react-components/src/useId.tsx';
import slugify from '~/techstyle-shared/react-components/src/utils/slugify';

import useProductGridData from '../../hooks/useProductGridData';

const ProductCardSwatchesMobileSwatchList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin: 8px 0;
`;

const SwatchButton = styled.button`
  padding: 0;
  border: none;
  background: none;
  font-family: inherit;
  cursor: pointer;
`;

const SwatchCircle = styled.div`
  position: relative;
  width: 26px;
  height: 26px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: transparent;

  // Inactive swatches: light gray border
  border: ${({ isActive }) =>
    isActive ? '2px solid transparent' : '2px solid rgb(237, 237, 237)'};

  &::before,
  &::after {
    content: '';
    position: absolute;
    border-radius: 50%;
    pointer-events: none;
  }

  // Inner white ring (adjusted for smaller circle)
  &::before {
    top: 4px;
    left: 4px;
    right: 4px;
    bottom: 4px;
    background: ${({ isActive }) => (isActive ? 'white' : 'transparent')};
    z-index: 1;
  }

  // Outer black ring
  &::after {
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    box-shadow: ${({ isActive }) => (isActive ? '0 0 0 2px black' : 'none')};
    z-index: 2;
  }
`;

const SwatchImage = styled.img`
  width: ${({ isActive }) => (isActive ? '18px' : '22px')};
  height: ${({ isActive }) => (isActive ? '18px' : '22px')};
  border-radius: 50%;
  position: relative;
  z-index: 3;
  background-color: ${({ isActive, theme }) =>
    isActive
      ? theme.colors.gridProductCardSwatchBackgroundSelected
      : 'transparent'};
`;
const MoreSwatchesLink = styled.a`
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding-top: 0.5px;
  color: ${({ theme }) => theme.colors.gridProductCardSwatchMoreSwatchesIcon};
`;

const StyledSkeleton = styled(SkeletonLine)`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: 1px solid
    ${({ theme }) => theme.colors.gridProductCardSwatchSkeletonBorder};
`;

const ProductCardSwatchesMobile = ({
  swatches = [],
  activeProductId,
  setActiveProductId = () => {},
  activeProductPdpLink,
}) => {
  const { isRelatedProductsLoading } = useProductGridData();
  const { matches } = useBreakpoint('(max-width: 410px)');
  const maxSwatchesToRender = matches ? 3 : 4; // prevent overflow into the next line
  const elementId = useId();

  const isActive = (productId) => productId === activeProductId;

  // Make sure to use isLoading instead of isFetching to avoid flickering on new batches of data on scroll
  // Works together with react-query's `keepPreviousData` option
  // TODO: use batch based loading state instead of blanket loading state
  if (isRelatedProductsLoading) {
    return (
      <ProductCardSwatchesMobileSwatchList>
        {Array.from({ length: maxSwatchesToRender + 1 }, (_, index) => (
          <StyledSkeleton key={index} />
        ))}
      </ProductCardSwatchesMobileSwatchList>
    );
  }

  return (
    <ProductCardSwatchesMobileSwatchList>
      {swatches?.slice(0, maxSwatchesToRender).map((swatch) => (
        <SwatchButton
          key={swatch?.productId}
          type="button"
          onClick={() => {
            if (swatch?.productId !== activeProductId) {
              setActiveProductId(swatch?.productId);
            }
          }}
          onMouseEnter={() => {
            if (swatch?.productId !== activeProductId) {
              setActiveProductId(swatch?.productId);
            }
          }}
          data-masterproductid={swatch?.productId}
          data-autotag={`${slugify(swatch?.label, '-')}-${elementId}`}
        >
          <SwatchCircle isActive={isActive(swatch?.productId)}>
            <SwatchImage
              src={swatch?.url}
              alt={swatch?.label}
              title={swatch?.label}
              isActive={isActive(swatch?.productId)}
            />
          </SwatchCircle>
        </SwatchButton>
      ))}
      {swatches.length > maxSwatchesToRender && (
        <Link href={activeProductPdpLink}>
          <MoreSwatchesLink>
            <PlusIcon size={18} />
          </MoreSwatchesLink>
        </Link>
      )}
    </ProductCardSwatchesMobileSwatchList>
  );
};

ProductCardSwatchesMobile.propTypes = {
  activeProductId: PropTypes.number,
  activeProductPdpLink: PropTypes.string,
  setActiveProductId: PropTypes.func,
  swatches: PropTypes.arrayOf(
    PropTypes.shape({
      productId: PropTypes.number.isRequired,
      url: PropTypes.string.isRequired,
      label: PropTypes.string,
    })
  ),
};

export default ProductCardSwatchesMobile;
